import { useState, useCallback } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { BackButton } from "../components/BackButton";

/**
 * AppLayout - Main app shell with slide-out navigation for authenticated users.
 *
 * Features:
 * - Hamburger menu in top-right corner
 * - Slide-out navigation panel from right (Play, Parent, Settings)
 * - Back button in header for navigation depth
 * - Large touch targets (64px+) for preschool accessibility
 * - Playful, child-friendly design
 */
export function AppLayout() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const openMenu = useCallback(() => setIsMenuOpen(true), []);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  // Determine if we should show the back button
  // Show on pages deeper than the main tabs
  const showBackButton = !["/profiles", "/play", "/parent", "/settings"].includes(
    location.pathname
  );

  // Get the page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/profiles") return "Who's Playing?";
    if (path === "/play") return "Let's Learn!";
    if (path === "/parent") return "Parent Dashboard";
    if (path === "/settings") return "Settings";
    if (path.startsWith("/play/")) {
      // Extract discipline from path for discipline/level pages
      const parts = path.split("/");
      const discipline = parts[2];
      if (parts[3]) return `Level ${parts[3]}`;
      return discipline ? discipline.charAt(0).toUpperCase() + discipline.slice(1) : "Play";
    }
    return "JumpBean";
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sunshine-100 via-cloud to-sky-100 flex flex-col">
      {/* Skip to main content link for keyboard/screen reader users */}
      <a
        href="#main-content"
        className="
          sr-only focus:not-sr-only
          focus:absolute focus:top-4 focus:left-4 focus:z-50
          focus:px-6 focus:py-3
          focus:bg-magic-600 focus:text-white
          focus:rounded-xl focus:font-display focus:font-bold
          focus:shadow-lg
        "
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="w-full px-4 py-4 flex items-center gap-4">
        {showBackButton && <BackButton />}
        <h1 className="font-display font-bold text-2xl text-magic-600 flex-1">
          {getPageTitle()}
        </h1>

        {/* Hamburger Menu Button */}
        <button
          type="button"
          onClick={openMenu}
          className="
            w-12 h-12 rounded-full
            flex items-center justify-center
            bg-magic-100 hover:bg-magic-200
            transition-colors duration-200
            shadow-md
          "
          aria-label="Open menu"
          aria-expanded={isMenuOpen}
          aria-controls="slide-menu"
        >
          <HamburgerIcon className="w-6 h-6 text-magic-600" />
        </button>
      </header>

      {/* Main content area - scrollable */}
      <main id="main-content" className="flex-1 overflow-auto" tabIndex={-1}>
        <Outlet />
      </main>

      {/* Slide-out Menu Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      {/* Slide-out Menu Panel */}
      <nav
        id="slide-menu"
        className={`
          fixed top-0 right-0 h-full w-72 max-w-[80vw]
          bg-cloud shadow-2xl z-50
          transform transition-transform duration-300 ease-in-out
          ${isMenuOpen ? "translate-x-0" : "translate-x-full"}
        `}
        aria-label="Main navigation"
        aria-hidden={!isMenuOpen}
      >
        {/* Menu Header with Close Button */}
        <div className="flex items-center justify-between p-4 border-b-2 border-neutral-200">
          <span className="font-display font-bold text-xl text-magic-600">Menu</span>
          <button
            type="button"
            onClick={closeMenu}
            className="
              w-12 h-12 rounded-full
              flex items-center justify-center
              bg-neutral-100 hover:bg-neutral-200
              transition-colors duration-200
            "
            aria-label="Close menu"
          >
            <CloseIcon className="w-6 h-6 text-neutral-600" />
          </button>
        </div>

        {/* Menu Items */}
        <div className="flex flex-col p-4 gap-2">
          <MenuItem to="/play" icon={<PlayIcon />} label="Play" onClick={closeMenu} />
          <MenuItem to="/parent" icon={<ParentIcon />} label="Parent" onClick={closeMenu} />
          <MenuItem to="/settings" icon={<SettingsIcon />} label="Settings" onClick={closeMenu} />
        </div>
      </nav>
    </div>
  );
}

interface MenuItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

function MenuItem({ to, icon, label, onClick }: MenuItemProps) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) => `
        flex items-center gap-4
        min-h-[64px] px-4 py-3
        rounded-xl
        transition-all duration-200
        ${
          isActive
            ? "bg-magic-100 text-magic-600"
            : "text-neutral-600 hover:bg-neutral-100 hover:text-magic-500"
        }
      `}
    >
      {({ isActive }) => (
        <>
          <span className="w-8 h-8" aria-hidden="true">{icon}</span>
          <span className="font-display font-semibold text-lg">{label}</span>
          {isActive && <span className="sr-only">(current page)</span>}
        </>
      )}
    </NavLink>
  );
}

// Icons
function HamburgerIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function CloseIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-full h-full"
      aria-hidden="true"
    >
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function ParentIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-full h-full"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M12 14c-5 0-9 2.5-9 6v2h18v-2c0-3.5-4-6-9-6z" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-full h-full"
      aria-hidden="true"
    >
      <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
    </svg>
  );
}

export default AppLayout;
